const PACKAGE_BUILDER_BLOCK_PACKAGE_ORDER = ['starter', 'explorer', 'adventurer', 'warrior'];
const PACKAGE_BUILDER_BLOCK_CURRENT_YEAR = new Date().getFullYear();
const PACKAGE_BUILDER_BLOCK_STOREFRONT_QUERY = `
  query PackageBuilderRows($type: String!, $first: Int!, $after: String) {
    metaobjects(type: $type, first: $first, after: $after, sortKey: "updated_at") {
      nodes {
        handle
        fields {
          key
          value
          type
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

class PackageBuilderMenuSection extends HTMLElement { 
  connectedCallback() {
    this.menu = this.querySelector('[ref="menu"]');
    this.panel = this.querySelector('[ref="panel"]');
    this.triggerButton = this.querySelector('[ref="triggerButton"]');
    this.triggerIcon = this.querySelector('[ref="triggerIcon"]');
    this.menuTitle = this.querySelector('[ref="menuTitle"]');
    this.backButton = this.querySelector('[ref="backButton"]');
    this.focusOverlay = this.querySelector('.package-builder-block__focus-overlay');
    this.selectedSummary = this.querySelector('[ref="selectedSummary"]');
    this.list = this.querySelector('[ref="list"]');
    this.results = this.querySelector('[ref="results"]');
    this.storefrontEndpoint = this.dataset.storefrontEndpoint || '';
    this.storefrontToken = this.dataset.storefrontToken || '';
    this.metaobjectType = this.dataset.metaobjectType || 'package_builder_package_v2';
    this.includeTestProducts = this.dataset.includeTestProducts === 'true';
    this.packageBuilderUrl = this.dataset.packageBuilderUrl || '/pages/package-builder';
    this.makeModelOrder = this.parseMakeModelOrder(this.dataset.makeModelOrder);
    this.packageRows = [];
    this.currentStep = 'make_model';
    this.stepHistory = [];
    this.isPanelOpen = false;
    this.isLoaded = false;
    this.selectedFitment = {
      make_model: '',
      year: '',
      series: '',
      engine: '',
    };

    if (!this.handleBackClickBound) {
      this.handleBackClickBound = this.handleBackClick.bind(this);
    }

    if (!this.handleTriggerClickBound) {
      this.handleTriggerClickBound = this.handleTriggerClick.bind(this);
    }

    if (!this.handleDocumentClickBound) {
      this.handleDocumentClickBound = this.handleDocumentClick.bind(this);
    }

    if (!this.handleWindowResizeBound) {
      this.handleWindowResizeBound = this.updateFocusOverlayHeight.bind(this);
    }

    if (!this.handle_touch_start_bound) {
      this.handle_touch_start_bound = this.handle_touch_start.bind(this);
    }

    if (!this.handle_touch_move_bound) {
      this.handle_touch_move_bound = this.handle_touch_move.bind(this);
    }

    this.backButton?.removeEventListener('click', this.handleBackClickBound);
    this.backButton?.addEventListener('click', this.handleBackClickBound);
    this.triggerButton?.removeEventListener('click', this.handleTriggerClickBound);
    this.triggerButton?.addEventListener('click', this.handleTriggerClickBound);
    document.removeEventListener('click', this.handleDocumentClickBound);
    document.addEventListener('click', this.handleDocumentClickBound);
    window.removeEventListener('resize', this.handleWindowResizeBound);
    window.addEventListener('resize', this.handleWindowResizeBound);
    this.add_scroll_touch_handlers(this.list);
    this.add_scroll_touch_handlers(this.results);

    if (!this.panelResizeObserver && window.ResizeObserver && this.panel) {
      this.panelResizeObserver = new ResizeObserver(() => {
        this.updateFocusOverlayHeight();
      });
      this.panelResizeObserver.observe(this.panel);
    }

    this.loadPackageRows();
  }

  disconnectedCallback() {
    this.backButton?.removeEventListener('click', this.handleBackClickBound);
    this.triggerButton?.removeEventListener('click', this.handleTriggerClickBound);
    document.removeEventListener('click', this.handleDocumentClickBound);
    window.removeEventListener('resize', this.handleWindowResizeBound);
    this.remove_scroll_touch_handlers(this.list);
    this.remove_scroll_touch_handlers(this.results);
    this.panelResizeObserver?.disconnect();
    this.panelResizeObserver = null;
  }

  add_scroll_touch_handlers(scroll_container) {
    if (!scroll_container) {
      return;
    }

    scroll_container.removeEventListener('touchstart', this.handle_touch_start_bound, true);
    scroll_container.removeEventListener('touchmove', this.handle_touch_move_bound, true);
    scroll_container.addEventListener('touchstart', this.handle_touch_start_bound, {
      capture: true,
      passive: true,
    });
    scroll_container.addEventListener('touchmove', this.handle_touch_move_bound, {
      capture: true,
      passive: false,
    });
  }

  remove_scroll_touch_handlers(scroll_container) {
    if (!scroll_container) {
      return;
    }

    scroll_container.removeEventListener('touchstart', this.handle_touch_start_bound, true);
    scroll_container.removeEventListener('touchmove', this.handle_touch_move_bound, true);
  }

  handle_touch_start(event) {
    this.touch_start_y = event.touches[0]?.clientY ?? 0;
  }

  handle_touch_move(event) {
    const scroll_container = event.currentTarget;
    const current_y = event.touches[0]?.clientY;

    if (!(scroll_container instanceof HTMLElement) || current_y === undefined) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const movement_y = this.touch_start_y - current_y;
    scroll_container.scrollTop += movement_y;
    this.touch_start_y = current_y;
  }

  async loadPackageRows() {
    if (!this.storefrontEndpoint || !this.storefrontToken) {
      console.error('Package builder block Storefront API token or endpoint is missing.');
      return;
    }

    try {
      const rawRows = await this.fetchAllMetaobjects();
      this.packageRows = rawRows
        .map((row) => this.normalizeMetaobjectRow(row))
        .filter((row) => row.is_available)
        .filter((row) => this.includeTestProducts || row.is_test !== true);
      this.showStep('make_model', false);
      this.setLoadingState(false);
      this.setPanelOpen(false);
    } catch (error) {
      console.error('Package builder block data load failed.', error);
    }
  }

  async fetchAllMetaobjects() {
    const allRows = [];
    let after = null;
    let hasNextPage = true;

    while (hasNextPage) {
      const response = await fetch(this.storefrontEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({
          query: PACKAGE_BUILDER_BLOCK_STOREFRONT_QUERY,
          variables: {
            type: this.metaobjectType,
            first: 250,
            after,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Storefront request failed with status ${response.status}.`);
      }

      const responseJson = await response.json();

      if (Array.isArray(responseJson.errors) && responseJson.errors.length > 0) {
        throw new Error(responseJson.errors.map((item) => item.message).join(' | '));
      }

      const connection = responseJson?.data?.metaobjects;

      if (!connection) {
        throw new Error('Storefront API did not return a metaobjects connection.');
      }

      allRows.push(...(connection.nodes || []));
      hasNextPage = Boolean(connection.pageInfo?.hasNextPage);
      after = connection.pageInfo?.endCursor || null;
    }

    return allRows;
  }

  handleTriggerClick(event) {
    event.stopPropagation();

    if (!this.isLoaded) {
      return;
    }

    if (this.hasProgressed()) {
      this.handleBackClick();
      return;
    }

    this.setPanelOpen(!this.isPanelOpen);
  }

  handleDocumentClick(event) {
    if (!this.isPanelOpen) {
      return;
    }

    if (this.contains(event.target)) {
      return;
    }

    this.setPanelOpen(false);
  }

  setPanelOpen(isOpen) {
    if (isOpen && !this.isLoaded) {
      return;
    }

    this.isPanelOpen = Boolean(isOpen);
    this.classList.toggle('package-builder-block--panel-open', this.isPanelOpen);

    if (this.panel) {
      this.panel.hidden = !this.isPanelOpen;
    }

    if (this.triggerButton) {
      this.triggerButton.setAttribute('aria-expanded', String(this.isPanelOpen));
    }

    this.updateFocusOverlayHeight();

    this.updateHeaderArrows();

    if (!this.menuTitle) {
      return;
    }

    if (this.isPanelOpen) {
      this.menuTitle.textContent = this.titleForStep(this.currentStep);
      return;
    }

    if (this.currentStep === 'make_model' && !this.selectedFitment.make_model) {
      this.menuTitle.textContent = 'Select Make & Model';
    }
  }

  setLoadingState(isLoading) {
    this.isLoaded = !isLoading;
    this.classList.toggle('package-builder-block--loading', isLoading);

    if (!this.triggerButton) {
      return;
    }

    this.triggerButton.disabled = isLoading;
    this.triggerButton.setAttribute('aria-busy', String(isLoading));
  }

  updateFocusOverlayHeight() {
    if (!this.focusOverlay) {
      return;
    }

    if (!this.isPanelOpen || !this.panel || this.panel.hidden) {
      this.style.setProperty('--package-builder-block-panel-current-height', '0px');
      this.style.setProperty('--package-builder-block-overlay-panel-height', '0px');
      return;
    }

    window.requestAnimationFrame(() => {
      let panelHeight = 0;

      for (const panelChild of this.panel.children) {
        if (panelChild.hidden) {
          continue;
        }

        panelHeight += Math.max(panelChild.offsetHeight, panelChild.scrollHeight);
      }

      const panelMaxHeight = parseFloat(window.getComputedStyle(this.panel).maxHeight);
      const visiblePanelHeight = Number.isNaN(panelMaxHeight)
        ? panelHeight
        : Math.min(panelHeight, panelMaxHeight);
      const section = this.closest('.shopify-section');
      const layout = this.querySelector('.package-builder-block__layout');

      if (section && layout) {
        const sectionRect = section.getBoundingClientRect();
        const layoutRect = layout.getBoundingClientRect();

        this.style.setProperty('--package-builder-block-section-overlay-top', `${sectionRect.top - layoutRect.top}px`);
        this.style.setProperty('--package-builder-block-section-overlay-left', `${sectionRect.left - layoutRect.left}px`);
        this.style.setProperty('--package-builder-block-section-overlay-width', `${sectionRect.width}px`);
        this.style.setProperty('--package-builder-block-section-overlay-height', `${sectionRect.height}px`);
      }

      this.style.setProperty('--package-builder-block-panel-current-height', `${panelHeight}px`);
      this.style.setProperty('--package-builder-block-overlay-panel-height', `${visiblePanelHeight}px`);
    });
  }

  normalizeMetaobjectRow(row) {
    const fields = {};

    for (const field of row.fields || []) {
      fields[field.key] = this.parseFieldValue(field);
    }

    const make = this.cleanValue(fields.make);
    const model = this.cleanValue(fields.model);
    const series = this.cleanValue(fields.series);
    const yearRange = this.cleanValue(fields.year_range);
    const packageName = this.cleanValue(fields.package_name || fields.title || '');
    const packageKey = this.handleize(fields.package_key || packageName);

    return {
      handle: row.handle || '',
      make,
      model,
      make_model: this.buildMakeModelLabel(make, model),
      series,
      engine: this.cleanValue(fields.engine),
      year_range: yearRange,
      years: this.expandYearRange(yearRange),
      package_name: packageName,
      package_key: packageKey,
      tier_label: packageName,
      tier_key: packageKey,
      is_available: fields.is_available !== false,
      is_test: fields.test === true,
      package_contents: Array.isArray(fields.package_contents) ? fields.package_contents : [],
      factory_power_kw: this.cleanValue(fields.factory_power_kw),
      factory_torque_nm: this.cleanValue(fields.factory_torque_nm),
      package_power_kw: this.cleanValue(fields.package_power_kw),
      package_torque_nm: this.cleanValue(fields.package_torque_nm),
      notes: this.cleanValue(fields.notes),
    };
  }

  parseFieldValue(field) {
    const rawValue = field?.value ?? '';
    const fieldType = field?.type || '';

    if (fieldType.startsWith('list.')) {
      try {
        const parsed = JSON.parse(rawValue);

        if (Array.isArray(parsed)) {
          return parsed.map((item) => this.cleanValue(item)).filter(Boolean);
        }
      } catch (error) {
        return rawValue
          .split(',')
          .map((item) => this.cleanValue(item))
          .filter(Boolean);
      }
    }

    if (fieldType === 'boolean') {
      return rawValue === 'true';
    }

    return rawValue;
  }

  handleBackClick() {
    if (!this.hasProgressed()) {
      return;
    }

    const previousStep = this.stepHistory.pop() || 'make_model';
    this.clearSelectionAfterStep(previousStep);
    this.showStep(previousStep, false);
    this.setPanelOpen(true);
  }

  hasProgressed() {
    return this.currentStep !== 'make_model' || this.stepHistory.length > 0;
  }

  clearSelectionAfterStep(step) {
    if (step === 'make_model') {
      this.selectedFitment.year = '';
      this.selectedFitment.series = '';
      this.selectedFitment.engine = '';
      return;
    }

    if (step === 'year') {
      this.selectedFitment.series = '';
      this.selectedFitment.engine = '';
      return;
    }

    if (step === 'series' || step === 'engine') {
      this.selectedFitment.engine = '';
    }
  }

  showStep(step, saveHistory = true) {
    if (saveHistory && this.currentStep !== step) {
      this.stepHistory.push(this.currentStep);
    }

    this.currentStep = step;
    this.menu.hidden = false;
    this.setPanelOpen(this.isPanelOpen);
    this.list.hidden = false;
    this.results.hidden = true;
    this.results.innerHTML = '';
    this.menuTitle.textContent = this.titleForStep(step);
    this.updateHeaderArrows();
    this.renderSelectedSummary();

    const options = this.optionsForStep(step);
    this.renderOptions(options);
    this.updateFocusOverlayHeight();
  }

  renderOptions(options) {
    this.list.innerHTML = '';

    if (options.length === 0) {
      const emptyMessage = document.createElement('p');
      emptyMessage.className = 'package-builder-block__empty';
      emptyMessage.textContent = 'No matching options found.';
      this.list.appendChild(emptyMessage);
      this.updateFocusOverlayHeight();
      return;
    }

    for (const option of options) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'package-builder-block__menu-row';
      button.textContent = option.label;
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.handleOptionSelect(option.value);
      });

      const arrow = document.createElement('span');
      arrow.className = 'package-builder-block__menu-arrow';
      arrow.textContent = '→';
      button.appendChild(arrow);

      this.list.appendChild(button);
    }

    this.updateFocusOverlayHeight();
  }

  handleOptionSelect(value) {
    this.setPanelOpen(true);

    if (this.currentStep === 'make_model') {
      this.selectedFitment.make_model = value;
      this.selectedFitment.year = '';
      this.selectedFitment.series = '';
      this.selectedFitment.engine = '';
      this.showStep('year');
      return;
    }

    if (this.currentStep === 'year') {
      this.selectedFitment.year = value;
      this.selectedFitment.series = '';
      this.selectedFitment.engine = '';
      this.showStep('series');
      return;
    }

    if (this.currentStep === 'series') {
      this.selectedFitment.series = value;
      this.selectedFitment.engine = '';
      this.selectEngineOrShowStep();
      return;
    }

    if (this.currentStep === 'engine') {
      this.selectedFitment.engine = value;
      this.redirectToPackageBuilder();
    }
  }

  selectEngineOrShowStep() {
    const engineOptions = this.getEngineOptions();

    if (engineOptions.length === 1) {
      this.selectedFitment.engine = engineOptions[0].value;
      this.redirectToPackageBuilder();
      return;
    }

    if (engineOptions.length === 0) {
      this.selectedFitment.engine = '';
      this.redirectToPackageBuilder();
      return;
    }

    this.showStep('engine');
  }

  redirectToPackageBuilder() {
    const selectedRows = this.getSelectedPackageRows();
    const selectedRow = selectedRows[0] || {};
    const targetUrl = new URL(this.packageBuilderUrl, window.location.origin);

    this.appendSearchParam(targetUrl, 'data_make', selectedRow.make);
    this.appendSearchParam(targetUrl, 'data_model', selectedRow.model);
    this.appendSearchParam(targetUrl, 'data_make_model', this.selectedFitment.make_model);
    this.appendSearchParam(targetUrl, 'data_year', this.selectedFitment.year);
    this.appendSearchParam(targetUrl, 'data_series', this.selectedFitment.series);
    this.appendSearchParam(targetUrl, 'data_engine', this.selectedFitment.engine);

    window.location.href = targetUrl.toString();
  }

  appendSearchParam(targetUrl, key, value) {
    const cleanValue = this.cleanValue(value);

    if (!cleanValue) {
      return;
    }

    targetUrl.searchParams.set(key, cleanValue);
  }

  renderPackages() {
    this.stepHistory.push(this.currentStep);
    this.currentStep = 'packages';
    this.menuTitle.textContent = 'YOUR POWER PACKAGES';
    this.setPanelOpen(true);
    this.updateHeaderArrows();
    this.list.hidden = true;
    this.results.hidden = false;
    this.results.innerHTML = '';
    this.renderSelectedSummary();

    const packageRows = this.sortPackages(this.getSelectedPackageRows());

    if (packageRows.length === 0) {
      const emptyMessage = document.createElement('p');
      emptyMessage.className = 'package-builder-block__empty';
      emptyMessage.textContent = 'No packages found for this vehicle.';
      this.results.appendChild(emptyMessage);
      return;
    }

    const packageGrid = document.createElement('div');
    packageGrid.className = 'package-builder-block__packages';

    for (const packageItem of packageRows) {
      packageGrid.appendChild(this.buildPackageCard(packageItem));
    }

    this.results.appendChild(packageGrid);
  }

  renderSelectedSummary() {
    const selectedParts = [
      this.selectedFitment.make_model,
      this.selectedFitment.year,
      this.selectedFitment.series,
      this.selectedFitment.engine,
    ].filter(Boolean);

    if (selectedParts.length === 0) {
      this.selectedSummary.hidden = true;
      this.selectedSummary.textContent = '';
      return;
    }

    this.selectedSummary.hidden = false;
    this.selectedSummary.textContent = selectedParts.join(' / ');
  }

  optionsForStep(step) {
    if (step === 'make_model') {
      return this.getMakeModelOptions();
    }

    if (step === 'year') {
      return this.getYearOptions();
    }

    if (step === 'series') {
      return this.getSeriesOptions();
    }

    if (step === 'engine') {
      return this.getEngineOptions();
    }

    return [];
  }

  getMakeModelOptions() {
    const optionSet = new Set();

    for (const row of this.packageRows) {
      if (row.make_model) {
        optionSet.add(row.make_model);
      }
    }

    return this.sortMakeModelOptions(optionSet);
  }

  getYearOptions() {
    const optionSet = new Set();

    for (const row of this.packageRows) {
      if (row.make_model !== this.selectedFitment.make_model) {
        continue;
      }

      for (const year of row.years) {
        optionSet.add(year);
      }
    }

    return [...optionSet]
      .sort((firstYear, secondYear) => Number(secondYear) - Number(firstYear))
      .map((year) => ({
        label: year,
        value: year,
      }));
  }

  getSeriesOptions() {
    const optionSet = new Set();

    for (const row of this.packageRows) {
      if (!this.rowMatchesSelectedYear(row)) {
        continue;
      }

      if (row.series) {
        optionSet.add(row.series);
      }
    }

    return this.setToSortedOptions(optionSet);
  }

  getEngineOptions() {
    const optionSet = new Set();

    for (const row of this.packageRows) {
      if (!this.rowMatchesSelectedSeries(row)) {
        continue;
      }

      if (row.engine) {
        optionSet.add(row.engine);
      }
    }

    return this.setToSortedOptions(optionSet);
  }

  getSelectedPackageRows() {
    const packageMap = new Map();

    for (const row of this.packageRows) {
      if (!this.rowMatchesSelectedEngine(row)) {
        continue;
      }

      const packageKey = [
        row.series,
        row.engine,
        row.package_key,
        row.package_power_kw,
        row.package_torque_nm,
      ].join('|');

      if (!packageMap.has(packageKey)) {
        packageMap.set(packageKey, row);
      }
    }

    return [...packageMap.values()];
  }

  rowMatchesSelectedYear(row) {
    return row.make_model === this.selectedFitment.make_model && row.years.includes(this.selectedFitment.year);
  }

  rowMatchesSelectedSeries(row) {
    return this.rowMatchesSelectedYear(row) && row.series === this.selectedFitment.series;
  }

  rowMatchesSelectedEngine(row) {
    if (!this.selectedFitment.engine) {
      return this.rowMatchesSelectedSeries(row);
    }

    return this.rowMatchesSelectedSeries(row) && row.engine === this.selectedFitment.engine;
  }

  setToSortedOptions(optionSet) {
    return [...optionSet]
      .sort((firstValue, secondValue) => firstValue.localeCompare(secondValue))
      .map((value) => ({
        label: value,
        value,
      }));
  }

  sortMakeModelOptions(optionSet) {
    const values = [...optionSet];

    if (this.makeModelOrder.length === 0) {
      return this.setToSortedOptions(optionSet);
    }

    const orderMap = new Map();

    for (const [index, makeModel] of this.makeModelOrder.entries()) {
      if (!orderMap.has(makeModel)) {
        orderMap.set(makeModel, index);
      }
    }

    return values
      .sort((firstValue, secondValue) => {
        const firstIndex = orderMap.has(firstValue)
          ? orderMap.get(firstValue)
          : Number.MAX_SAFE_INTEGER;
        const secondIndex = orderMap.has(secondValue)
          ? orderMap.get(secondValue)
          : Number.MAX_SAFE_INTEGER;

        if (firstIndex !== secondIndex) {
          return firstIndex - secondIndex;
        }

        return firstValue.localeCompare(secondValue);
      })
      .map((value) => ({
        label: value,
        value,
      }));
  }

  parseMakeModelOrder(rawValue) {
    return String(rawValue || '')
      .split(/\r?\n/)
      .map((value) => this.cleanValue(value))
      .filter(Boolean);
  }

  titleForStep(step) {
    const titleMap = {
      make_model: 'Select Make & Model',
      year: 'Select Year',
      series: 'Select Series',
      engine: 'Select Engine',
      packages: 'Your Power Packages',
    };

    return titleMap[step] || 'Select Vehicle';
  }

  sortPackages(packages) {
    if (!Array.isArray(packages)) {
      return [];
    }

    return [...packages].sort((firstPackage, secondPackage) => {
      const firstIndex = PACKAGE_BUILDER_BLOCK_PACKAGE_ORDER.indexOf(firstPackage?.tier_key);
      const secondIndex = PACKAGE_BUILDER_BLOCK_PACKAGE_ORDER.indexOf(secondPackage?.tier_key);
      const safeFirstIndex = firstIndex === -1 ? PACKAGE_BUILDER_BLOCK_PACKAGE_ORDER.length : firstIndex;
      const safeSecondIndex = secondIndex === -1 ? PACKAGE_BUILDER_BLOCK_PACKAGE_ORDER.length : secondIndex;

      return safeFirstIndex - safeSecondIndex;
    });
  }

  buildPackageCard(packageItem) {
    const normalizedPackage = this.normalizePackageItem(packageItem);
    const card = document.createElement('article');
    card.className = 'package-builder-block__package';
    card.dataset.available = normalizedPackage.is_available ? 'true' : 'false';

    const header = document.createElement('div');
    header.className = 'package-builder-block__package-header';

    const title = document.createElement('h3');
    title.className = 'package-builder-block__package-title';
    title.textContent = normalizedPackage.tier_label || normalizedPackage.package_name || 'Package';

    const badge = document.createElement('span');
    badge.className = 'package-builder-block__package-badge';
    badge.textContent = normalizedPackage.is_available ? 'Selected' : 'Unavailable';

    header.append(title, badge);
    card.appendChild(header);

    const specs = document.createElement('div');
    specs.className = 'package-builder-block__specs';
    specs.appendChild(this.buildSpec('Factory power', normalizedPackage.factory_power_kw, 'kW'));
    specs.appendChild(this.buildSpec('Package power', normalizedPackage.package_power_kw, 'kW'));
    specs.appendChild(this.buildSpec('Package torque', normalizedPackage.package_torque_nm, 'Nm'));
    card.appendChild(specs);

    const packageContents = Array.isArray(normalizedPackage.package_contents) ? normalizedPackage.package_contents : [];

    if (packageContents.length > 0) {
      const contentsTitle = document.createElement('h4');
      contentsTitle.className = 'package-builder-block__content-title';
      contentsTitle.textContent = 'Package contents';
      card.appendChild(contentsTitle);

      const contentsList = document.createElement('ul');
      contentsList.className = 'package-builder-block__content-list';

      for (const contentItem of packageContents) {
        const listItem = document.createElement('li');
        const optionLabel = document.createElement('label');
        optionLabel.className = 'package-builder-block__content-option';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = true;
        checkbox.disabled = !normalizedPackage.is_available;

        const optionText = document.createElement('span');
        optionText.textContent = contentItem;

        optionLabel.append(checkbox, optionText);
        listItem.appendChild(optionLabel);
        contentsList.appendChild(listItem);
      }

      card.appendChild(contentsList);
    }

    if (normalizedPackage.notes) {
      const note = document.createElement('p');
      note.className = 'package-builder-block__note';
      note.textContent = normalizedPackage.notes;
      card.appendChild(note);
    }

    return card;
  }

  normalizePackageItem(packageItem) {
    const normalizedPackage = {
      ...packageItem,
      tier_key: packageItem?.tier_key || packageItem?.package_key || '',
      tier_label: packageItem?.tier_label || packageItem?.package_name || '',
      is_available: packageItem?.is_available !== false,
      notes: packageItem?.notes || '',
      package_contents: Array.isArray(packageItem?.package_contents) ? [...packageItem.package_contents] : [],
    };

    if (
      normalizedPackage.is_available === false &&
      normalizedPackage.notes === '' &&
      normalizedPackage.package_contents.length === 1
    ) {
      const availabilityMessage = normalizedPackage.package_contents[0] || '';

      if (availabilityMessage.toLowerCase().includes('not available')) {
        normalizedPackage.notes = availabilityMessage;
        normalizedPackage.package_contents = [];
      }
    }

    return normalizedPackage;
  }

  buildSpec(label, value, suffix) {
    const spec = document.createElement('div');
    spec.className = 'package-builder-block__spec';

    const specLabel = document.createElement('span');
    specLabel.className = 'package-builder-block__spec-label';
    specLabel.textContent = label;

    const specValue = document.createElement('span');
    specValue.className = 'package-builder-block__spec-value';
    specValue.textContent = value ? `${value} ${suffix}` : 'Not provided';

    spec.append(specLabel, specValue);
    return spec;
  }

  expandYearRange(yearRange) {
    const bounds = this.parseYearRange(yearRange);

    if (!bounds) {
      return [];
    }

    const years = [];

    for (let year = bounds.start; year <= bounds.end; year += 1) {
      years.push(String(year));
    }

    return years;
  }

  parseYearRange(yearRange) {
    const cleanYearRange = this.cleanValue(yearRange);
    const yearMatches = cleanYearRange.match(/\d{4}/g) || [];

    if (yearMatches.length === 0) {
      return null;
    }

    const start = Number(yearMatches[0]);
    let end = start;

    if (cleanYearRange.toLowerCase().includes('current')) {
      end = PACKAGE_BUILDER_BLOCK_CURRENT_YEAR;
    } else if (yearMatches.length > 1) {
      end = Number(yearMatches[yearMatches.length - 1]);
    }

    if (Number.isNaN(start) || Number.isNaN(end)) {
      return null;
    }

    if (end < start) {
      return {
        start,
        end: start,
      };
    }

    return {
      start,
      end,
    };
  }

  buildMakeModelLabel(make, model) {
    const cleanMake = this.cleanValue(make);
    const cleanModel = this.cleanValue(model);

    return [cleanMake, cleanModel].filter(Boolean).join(' ');
  }

  cleanValue(value) {
    return String(value || '').trim();
  }

  handleize(value) {
    return this.cleanValue(value)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
  }

  updateHeaderArrows() {
    if (this.backButton) {
      this.backButton.hidden = true;
    }

    if (this.triggerIcon) {
      this.triggerIcon.hidden = false;
      this.triggerIcon.textContent = this.isPanelOpen ? '←' : '→';
    }
  }
}

if (!customElements.get('package-builder-menu-section')) {
  customElements.define('package-builder-menu-section', PackageBuilderMenuSection);
}
